import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  CircularProgress,
  Alert,
  Chip,
  Tooltip,
  Grid,
  Card,
  CardContent,
  Divider,
  InputAdornment
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Info as InfoIcon,
  Warning as WarningIcon,
  Edit as EditIcon,
  Key as KeyIcon
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import apiService from '../services/api';
import CloudInitConfigForm from '../components/CloudInitConfigForm';

const CloudInitPage = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(true);
  const [cloudInitIsos, setCloudInitIsos] = useState([]);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [infoDialogOpen, setInfoDialogOpen] = useState(false);
  const [selectedIso, setSelectedIso] = useState(null);
  const [isoUsageInfo, setIsoUsageInfo] = useState({});
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingIso, setEditingIso] = useState(null);
  const [hostSSHKey, setHostSSHKey] = useState(null);

  // Cloud-init 초기 설정 상수
  const INITIAL_CONFIG = {
    hostname: '',
    username: 'user',
    password: '',
    ssh_authorized_keys: [],
    timezone: 'Asia/Seoul',
    locale: 'ko_KR.UTF-8',
    packages: [],
    runcmd: [],
    enable_ssh: true,
    ssh_password_auth: true,
    ssh_key_auth: true,
    ssh_root_login: false,
    ssh_port: 22
  };

  // Cloud-init 설정 상태
  const [cloudInitConfig, setCloudInitConfig] = useState(INITIAL_CONFIG);

  // 설정 초기화 함수
  const resetCloudInitConfig = () => {
    setCloudInitConfig({ ...INITIAL_CONFIG });
  };

  // Cloud-init ISO 목록 로드
  const loadCloudInitIsos = async () => {
    setLoading(true);
    try {
      const response = await apiService.getCloudInitIsos();
      const isos = response.data.isos || [];
      setCloudInitIsos(isos);

      // 각 ISO의 사용 정보 확인
      const usageInfo = {};
      for (const iso of isos) {
        try {
          const usageResponse = await apiService.checkCloudInitIsoInUse(iso.name);
          usageInfo[iso.name] = usageResponse.data;
        } catch (err) {
          // ISO 사용 정보 확인 실패 무시
          usageInfo[iso.name] = { in_use: false, using_vms: [] };
        }
      }
      setIsoUsageInfo(usageInfo);

      // 호스트 SSH 키 가져오기
      try {
        const hostKeyResponse = await apiService.getHostSSHKey();
        if (hostKeyResponse.data.status === 'exists') {
          setHostSSHKey(hostKeyResponse.data);
        }
      } catch (error) {
        // 호스트 SSH 키는 선택사항이므로 오류 무시
      }

    } catch (error) {
      enqueueSnackbar('Cloud-init ISO 목록을 불러오는 중 오류가 발생했습니다.', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCloudInitIsos();
    
    // 페이지 포커스 시 자동 새로고침
    const handleFocus = () => {
      loadCloudInitIsos();
    };
    
    // 페이지 visibility 변경 시 새로고침
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        loadCloudInitIsos();
      }
    };
    
    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  // Cloud-init 설정 입력 변경 처리
  const handleConfigChange = (e) => {
    const { name, value, checked } = e.target;
    setCloudInitConfig(prev => ({
      ...prev,
      [name]: name.includes('ssh_') && name !== 'ssh_port' ? checked : value
    }));
  };

  // SSH 키 입력 변경 처리
  const handleSshKeysChange = (e) => {
    const keys = e.target.value.split('\n');
    setCloudInitConfig(prev => ({
      ...prev,
      ssh_authorized_keys: keys
    }));
  };

  // 패키지 입력 변경 처리
  const handlePackagesChange = (e) => {
    const packages = e.target.value.split('\n');
    setCloudInitConfig(prev => ({
      ...prev,
      packages: packages
    }));
  };

  // 실행 명령 입력 변경 처리
  const handleRuncmdChange = (e) => {
    const commands = e.target.value.split('\n');
    setCloudInitConfig(prev => ({
      ...prev,
      runcmd: commands
    }));
  };

  // 호스트 SSH 키 로드
  const loadHostSSHKey = () => {
    if (hostSSHKey && hostSSHKey.public_key) {
      const currentKeys = Array.isArray(cloudInitConfig.ssh_authorized_keys) 
        ? cloudInitConfig.ssh_authorized_keys 
        : cloudInitConfig.ssh_authorized_keys ? [cloudInitConfig.ssh_authorized_keys] : [];
      
      // 중복 키 체크
      if (!currentKeys.includes(hostSSHKey.public_key)) {
        setCloudInitConfig(prev => ({
          ...prev,
          ssh_authorized_keys: [...currentKeys, hostSSHKey.public_key]
        }));
        enqueueSnackbar('호스트 SSH 키가 추가되었습니다.', { variant: 'success' });
      } else {
        enqueueSnackbar('호스트 SSH 키가 이미 추가되어 있습니다.', { variant: 'info' });
      }
    } else {
      enqueueSnackbar('호스트 SSH 키를 찾을 수 없습니다.', { variant: 'warning' });
    }
  };

  // Cloud-init ISO 생성/편집
  const handleCreateCloudInit = async () => {
    if (!cloudInitConfig.hostname || !cloudInitConfig.username || !cloudInitConfig.password) {
      enqueueSnackbar('호스트명, 사용자명, 비밀번호는 필수입니다.', { variant: 'warning' });
      return;
    }

    // 빈 항목들을 필터링한 설정 생성
    const filteredConfig = {
      ...cloudInitConfig,
      ssh_authorized_keys: Array.isArray(cloudInitConfig.ssh_authorized_keys)
        ? cloudInitConfig.ssh_authorized_keys.filter(key => key.trim())
        : [],
      packages: Array.isArray(cloudInitConfig.packages)
        ? cloudInitConfig.packages.filter(pkg => pkg.trim())
        : [],
      runcmd: Array.isArray(cloudInitConfig.runcmd)
        ? cloudInitConfig.runcmd.filter(cmd => cmd.trim())
        : []
    };

    setCreating(true);
    try {
      if (editingIso) {
        // 편집 모드 - 기존 ISO 삭제 후 새로 생성
        await apiService.deleteCloudInitIso(editingIso.name, true);
        await apiService.createCloudInit(filteredConfig);
        enqueueSnackbar('Cloud-init ISO가 성공적으로 수정되었습니다.', { variant: 'success' });
        setEditDialogOpen(false);
      } else {
        // 생성 모드
        await apiService.createCloudInit(filteredConfig);
        enqueueSnackbar('Cloud-init ISO가 성공적으로 생성되었습니다.', { variant: 'success' });
        setCreateDialogOpen(false);
      }

      // 설정 초기화
      resetCloudInitConfig();
      setEditingIso(null);

      // 목록 다시 로드
      loadCloudInitIsos();
    } catch (error) {

      enqueueSnackbar(`Cloud-init ISO ${editingIso ? '수정' : '생성'} 실패: ${error.response?.data?.detail || error.message}`, { variant: 'error' });
    } finally {
      setCreating(false);
    }
  };

  // 편집 다이얼로그 열기
  const handleOpenEditDialog = async (iso) => {
    try {
      // 먼저 상태 초기화
      resetCloudInitConfig();
      setEditingIso(iso);

      // ISO 파일에서 실제 정보 가져오기
      const response = await apiService.getCloudInitIsoInfo(iso.name);

      if (response.data.status === 'success' && response.data.iso_info.user_data) {
        const userData = response.data.iso_info.user_data;

        setCloudInitConfig({
          hostname: userData.hostname || iso.name.replace('-cloud-init.iso', ''),
          username: userData.username || 'user',
          password: '', // 비밀번호는 해시되어 있어 복구 불가
          ssh_authorized_keys: userData.ssh_authorized_keys || [],
          timezone: userData.timezone || 'Asia/Seoul',
          locale: userData.locale || 'ko_KR.UTF-8',
          packages: userData.packages || [],
          runcmd: userData.runcmd || [],
          enable_ssh: userData.enable_ssh !== undefined ? userData.enable_ssh : true,
          ssh_password_auth: userData.ssh_password_auth !== undefined ? userData.ssh_password_auth : true,
          ssh_key_auth: userData.ssh_key_auth !== undefined ? userData.ssh_key_auth : true,
          ssh_root_login: userData.ssh_root_login !== undefined ? userData.ssh_root_login : false,
          ssh_port: userData.ssh_port || 22
        });

        enqueueSnackbar('ISO 정보를 성공적으로 불러왔습니다.', { variant: 'success' });
      } else {
        // 실패 시 기본값으로 설정 (이미 초기화했으므로 hostname만 설정)
        setCloudInitConfig(prev => ({
          ...prev,
          hostname: iso.name.replace('-cloud-init.iso', '')
        }));

        enqueueSnackbar('ISO 정보를 불러오지 못했습니다. 기본값을 사용합니다.', { variant: 'warning' });
      }

      setEditDialogOpen(true);
    } catch (error) {
      resetCloudInitConfig();
      setEditingIso(null);
      enqueueSnackbar('ISO 편집을 시작할 수 없습니다: ' + (error.response?.data?.detail || error.message), { variant: 'error' });
    }
  };

  // ISO 삭제 다이얼로그 열기
  const handleDeleteDialogOpen = (iso) => {
    setSelectedIso(iso);
    setDeleteDialogOpen(true);
  };

  // ISO 정보 다이얼로그 열기
  const handleInfoDialogOpen = (iso) => {
    setSelectedIso(iso);
    setInfoDialogOpen(true);
  };

  // ISO 삭제
  const handleDeleteIso = async (force = false) => {
    if (!selectedIso) return;

    setDeleting(true);
    try {
      await apiService.deleteCloudInitIso(selectedIso.name, force);
      enqueueSnackbar(`Cloud-init ISO '${selectedIso.name}'이 삭제되었습니다.`, { variant: 'success' });
      setDeleteDialogOpen(false);
      setSelectedIso(null);
      loadCloudInitIsos();
    } catch (error) {
      
      enqueueSnackbar(`Cloud-init ISO 삭제 실패: ${error.response?.data?.detail || error.message}`, { variant: 'error' });
    } finally {
      setDeleting(false);
    }
  };

  // ISO 사용 상태 표시
  const getUsageChip = (isoName) => {
    const usage = isoUsageInfo[isoName];
    if (!usage) return <Chip label="확인 중" size="small" />;
    
    if (usage.in_use) {
      return (
        <Tooltip title={`사용 중인 VM: ${usage.using_vms.map(vm => vm.name).join(', ')}`}>
          <Chip 
            label={`${usage.using_vms.length}개 VM에서 사용 중`} 
            color="warning" 
            size="small"
            icon={<WarningIcon />}
          />
        </Tooltip>
      );
    } else {
      return <Chip label="사용 안함" color="success" size="small" />;
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3, alignItems: 'center' }}>
        <Typography variant="h4">Cloud-init ISO 관리</Typography>
        <Box>
          <Button 
            variant="outlined" 
            startIcon={<RefreshIcon />}
            onClick={loadCloudInitIsos}
            sx={{ mr: 1 }}
          >
            새로고침
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              resetCloudInitConfig();
              setEditingIso(null);
              setCreateDialogOpen(true);
            }}
          >
            새 ISO 생성
          </Button>
        </Box>
      </Box>

      <TableContainer component={Paper}>
        <Table sx={{ minWidth: 650 }}>
          <TableHead>
            <TableRow>
              <TableCell>파일명</TableCell>
              <TableCell>호스트명</TableCell>
              <TableCell>파일 크기</TableCell>
              <TableCell>생성 시간</TableCell>
              <TableCell>사용 상태</TableCell>
              <TableCell align="center">작업</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {cloudInitIsos.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography variant="body1" sx={{ py: 2 }}>
                    Cloud-init ISO 파일이 없습니다.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              cloudInitIsos.map((iso) => (
                <TableRow key={iso.name} hover>
                  <TableCell sx={{ fontFamily: 'monospace' }}>{iso.name}</TableCell>
                  <TableCell>{iso.hostname || 'N/A'}</TableCell>
                  <TableCell>
                    {iso.size ? `${(iso.size / 1024 / 1024).toFixed(2)} MB` : 'N/A'}
                  </TableCell>
                  <TableCell>
                    {iso.created_time ? new Date(iso.created_time * 1000).toLocaleString() : 'N/A'}
                  </TableCell>
                  <TableCell>
                    {getUsageChip(iso.name)}
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="ISO 정보">
                      <IconButton onClick={() => handleInfoDialogOpen(iso)}>
                        <InfoIcon />
                      </IconButton>
                    </Tooltip>
                    
                    {/* 편집 버튼 - 사용 중이 아닌 ISO만 편집 가능 */}
                    {!isoUsageInfo[iso.name]?.in_use && (
                      <Tooltip title="ISO 편집">
                        <IconButton onClick={() => handleOpenEditDialog(iso)} color="primary">
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                    <Tooltip title="ISO 삭제">
                      <IconButton 
                        color="error"
                        onClick={() => handleDeleteDialogOpen(iso)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Cloud-init ISO 생성 다이얼로그 */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>새 Cloud-init ISO 생성</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Grid container spacing={3}>
              <CloudInitConfigForm
                config={cloudInitConfig}
                onConfigChange={handleConfigChange}
                onSshKeysChange={handleSshKeysChange}
                onPackagesChange={handlePackagesChange}
                onRuncmdChange={handleRuncmdChange}
                hostSSHKey={hostSSHKey}
                onLoadHostSSHKey={loadHostSSHKey}
                showAdvanced={true}
              />
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setCreateDialogOpen(false);
            resetCloudInitConfig();
          }}>취소</Button>
          <Button
            onClick={handleCreateCloudInit}
            variant="contained"
            disabled={creating || !cloudInitConfig.hostname || !cloudInitConfig.username || !cloudInitConfig.password}
          >
            {creating ? '생성 중...' : 'ISO 생성'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ISO 삭제 확인 다이얼로그 */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Cloud-init ISO 삭제 확인</DialogTitle>
        <DialogContent>
          {selectedIso && (
            <Box>
              <Typography variant="body1" gutterBottom>
                정말 Cloud-init ISO '{selectedIso.name}'을 삭제하시겠습니까?
              </Typography>
              
              {isoUsageInfo[selectedIso.name]?.in_use && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  <Typography variant="body2" gutterBottom>
                    <strong>주의:</strong> 이 ISO는 현재 다음 VM에서 사용 중입니다:
                  </Typography>
                  <Box sx={{ ml: 2 }}>
                    {isoUsageInfo[selectedIso.name].using_vms.map(vm => (
                      <Typography key={vm.name} variant="body2">
                        • {vm.name}
                      </Typography>
                    ))}
                  </Box>
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    강제 삭제하면 해당 VM들에서 Cloud-init 기능을 사용할 수 없습니다.
                  </Typography>
                </Alert>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>취소</Button>
          {selectedIso && isoUsageInfo[selectedIso.name]?.in_use && (
            <Button 
              onClick={() => handleDeleteIso(true)} 
              color="error"
              disabled={deleting}
            >
              {deleting ? '삭제 중...' : '강제 삭제'}
            </Button>
          )}
          <Button 
            onClick={() => handleDeleteIso(false)} 
            color="error" 
            variant="contained"
            disabled={deleting}
          >
            {deleting ? '삭제 중...' : '삭제'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ISO 정보 다이얼로그 */}
      <Dialog 
        open={infoDialogOpen} 
        onClose={() => setInfoDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Cloud-init ISO 정보</DialogTitle>
        <DialogContent>
          {selectedIso && (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  {selectedIso.name}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  파일 크기
                </Typography>
                <Typography variant="body1">
                  {selectedIso.size}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  생성 날짜
                </Typography>
                <Typography variant="body1">
                  {selectedIso.created_time ? new Date(selectedIso.created_time * 1000).toLocaleString() : 'N/A'}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  호스트명
                </Typography>
                <Typography variant="body1">
                  {selectedIso.hostname}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary">
                  사용 상태
                </Typography>
                <Typography variant="body1">
                  {getUsageChip(selectedIso.name)}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary">
                  파일 경로
                </Typography>
                <Typography variant="body1" sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
                  {selectedIso.path}
                </Typography>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInfoDialogOpen(false)}>닫기</Button>
        </DialogActions>
      </Dialog>

      {/* Cloud-init ISO 편집 다이얼로그 */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Cloud-init ISO 편집</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Grid container spacing={3}>
              <CloudInitConfigForm
                config={cloudInitConfig}
                onConfigChange={handleConfigChange}
                onSshKeysChange={handleSshKeysChange}
                onPackagesChange={handlePackagesChange}
                onRuncmdChange={handleRuncmdChange}
                hostSSHKey={hostSSHKey}
                onLoadHostSSHKey={loadHostSSHKey}
                showAdvanced={true}
              />
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setEditDialogOpen(false);
            resetCloudInitConfig();
            setEditingIso(null);
          }}>취소</Button>
          <Button
            onClick={handleCreateCloudInit}
            variant="contained"
            disabled={creating || !cloudInitConfig.hostname || !cloudInitConfig.username || !cloudInitConfig.password}
          >
            {creating ? '수정 중...' : 'ISO 수정'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CloudInitPage;